import type { ONUListItem, ONUListResponse } from '../devicesApi';
import { buildOnuRowKey, buildPonKeyFromIds } from '../../utils/ponKeys';

export interface NormalizedOnuListItem extends ONUListItem {
  __ponKey: string | null;
  __rowKey: string;
  __vendorModel?: string;
}

export interface NormalizedOnuListMeta {
  keyIndex: Record<string, number>;
  rowKeyIndex: Record<string, number>;
  latestUpdatedAt: string | null;
}

export interface NormalizedOnuListResponse extends Omit<ONUListResponse, 'items'> {
  items: NormalizedOnuListItem[];
  meta: NormalizedOnuListMeta;
}

export function normalizeOnuListResponse(response: ONUListResponse): NormalizedOnuListResponse {
  const keyIndex: Record<string, number> = {};
  const rowKeyIndex: Record<string, number> = {};
  let latestUpdatedAt: string | null = null;

  const normalizedItems: NormalizedOnuListItem[] = (response.items ?? []).map((item, index) => {
    const ponKey = buildPonKeyFromIds(item.unique_id, item.olt_if_index, item.nr);
    if (ponKey) {
      keyIndex[ponKey] = index;
    }
    const rowKey = buildOnuRowKey(item, ponKey, index);
    rowKeyIndex[rowKey] = index;

    const normalizedUpdatedAt = normalizeDate(item.pon_updated_at ?? item.updated_at);
    if (normalizedUpdatedAt && (!latestUpdatedAt || normalizedUpdatedAt > latestUpdatedAt)) {
      latestUpdatedAt = normalizedUpdatedAt;
    }
    const vendorModel =
      [item.vendor, (item as any).model].filter((v) => typeof v === 'string' && v.trim().length > 0).join(' ') || undefined;

    return {
      ...item,
      pon_updated_at: normalizedUpdatedAt ?? item.pon_updated_at ?? item.updated_at,
      __ponKey: ponKey,
      __rowKey: rowKey,
      __vendorModel: vendorModel,
    };
  });

  const statistics = response.statistics ?? computeStatistics(normalizedItems);

  return {
    ...response,
    items: normalizedItems,
    statistics,
    meta: {
      keyIndex,
      rowKeyIndex,
      latestUpdatedAt,
    },
  };
}

function normalizeDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toISOString();
}

function computeStatistics(list: NormalizedOnuListItem[]) {
  if (list.length === 0) {
    return {
      total: 0,
      online: 0,
      offline: 0,
      unknown: 0,
      bad_level: 0,
    };
  }

  let online = 0;
  let offline = 0;
  let unknown = 0;
  let bad = 0;

  for (const onu of list) {
    if (onu.is_unknown) {
      unknown += 1;
    } else if (onu.is_online) {
      online += 1;
    } else {
      offline += 1;
    }
    if (onu.is_level_bad) {
      bad += 1;
    }
  }

  return {
    total: list.length,
    online,
    offline,
    unknown,
    bad_level: bad,
  };
}

